import { type Dispatch, type SetStateAction, useMemo, useRef, useState } from 'react';
import type { TableColumns } from '@ff-it/ui';
import type { ControlledTableColumns } from './types';

export type PinSide = 'left' | 'right';

export type ControlledTableColumnConfig<R> = {
  column: TableColumns<R>[number];
  configLabel?: string;
  required?: boolean;
  visible: boolean;
  pinned?: PinSide;
};

const COLUMN_CONFIG_THRESHOLD = 3;

function usePersistentState<T>(key: string | undefined, defaultValue: T): [T, Dispatch<SetStateAction<T>>] {
  const read = (): T => {
    if (!key) {
      return defaultValue;
    }
    try {
      const raw = localStorage.getItem(key);
      return raw === null ? defaultValue : (JSON.parse(raw) as T);
    } catch {
      return defaultValue;
    }
  };
  const [state, setState] = useState<T>(read);

  const prevKey = useRef(key);
  if (prevKey.current !== key) {
    prevKey.current = key;
    setState(read());
  }

  const setPersisted: Dispatch<SetStateAction<T>> = (value) => {
    setState((prev) => {
      const next = typeof value === 'function' ? (value as (p: T) => T)(prev) : value;
      if (key) {
        try {
          localStorage.setItem(key, JSON.stringify(next));
        } catch {
          // ignore quota / serialization errors
        }
      }
      return next;
    });
  };

  return [state, setPersisted];
}

export function useColumnConfig<R>(
  storageKey: string | undefined,
  columns: ControlledTableColumns<R>,
): {
  configuredColumns: ControlledTableColumnConfig<R>[];
  configurable: boolean;
  hasLeftPin: boolean;
  hasRightPin: boolean;
  columnActions: {
    toggleColumn: (key: string) => void;
    pinColumn: (key: string, side: PinSide | null) => void;
    resetColumns: () => void;
  };
} {
  const [hidden, setHidden] = usePersistentState<string[]>(storageKey, []);
  const [pins, setPins] = usePersistentState<Record<string, PinSide>>(
    storageKey ? `${storageKey}::pins` : undefined,
    {},
  );

  const configurable = columns.length > COLUMN_CONFIG_THRESHOLD;

  const configuredColumns = useMemo<ControlledTableColumnConfig<R>[]>(() => {
    const hiddenSet = new Set(Array.isArray(hidden) ? hidden : []);
    const pinsMap = pins && typeof pins === 'object' ? pins : {};
    return columns.map((c) => {
      const { required, configLabel, ...column } = c;
      const key = String(column.key);
      return {
        column,
        configLabel,
        required,
        visible: !configurable || required === true || !hiddenSet.has(key),
        pinned: configurable ? pinsMap[key] : undefined,
      };
    });
  }, [columns, hidden, pins, configurable]);

  const hasLeftPin = configuredColumns.some((c) => c.visible && c.pinned === 'left');
  const hasRightPin = configuredColumns.some((c) => c.visible && c.pinned === 'right');

  const toggleColumn = (key: string): void => {
    const hiding = !hidden.includes(key);
    setHidden((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
    if (hiding) {
      setPins((prev) => {
        const { [key]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };
  const pinColumn = (key: string, side: PinSide | null): void =>
    setPins((prev) => {
      if (!side) {
        const { [key]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: side };
    });
  const resetColumns = (): void => {
    setHidden([]);
    setPins({});
  };

  return {
    configuredColumns,
    configurable,
    hasLeftPin,
    hasRightPin,
    columnActions: { toggleColumn, pinColumn, resetColumns },
  };
}
